import { FormEvent, useState } from "react";
import { FetcherWithComponents } from "react-router";
import { AccountsByApplication, OAuthClient } from "../infrastructure/api/api";
import { TranslationKey } from "../infrastructure/translation/translator";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import { isClientActionFailure } from "./clientsRouteContext";
import { hasAnyScope, parseScopes, toggleScope } from "./scopeUtils";

type ClientFormValues = {
  displayName: string;
  certificateIds: string[];
  scopes: AccountsByApplication;
};

export interface ClientFormState {
  displayName: string;
  setDisplayName: (displayName: string) => void;
  selectedScopes: AccountsByApplication;
  onScopeChange: (oAuthId: string, account: string, checked: boolean) => void;
  hasSelectedScopes: boolean;
  selectedCertificateIds: string[];
  onCertificateChange: (certificateId: string, checked: boolean) => void;
  submitting: boolean;
  error: string | null;
  onSubmit: (e: FormEvent) => void;
}

interface Options {
  client?: OAuthClient;
  selectableCertificateIds?: string[];
  missingEndpointTextKey: TranslationKey;
  fetcher: FetcherWithComponents<unknown>;
}

const keepSelectable = (certificateIds: string[], selectable?: string[]) =>
  selectable === undefined
    ? certificateIds
    : certificateIds.filter((id) => selectable.includes(id));

export const useClientForm = ({
  client,
  selectableCertificateIds,
  missingEndpointTextKey,
  fetcher,
}: Options): ClientFormState => {
  const translate = useTranslate();

  const [displayName, setDisplayName] = useState(client?.displayName ?? "");
  const [selectedScopes, setSelectedScopes] = useState<AccountsByApplication>(
    () => (client ? parseScopes(client.scopes) : {}),
  );
  const [certificateIds, setCertificateIds] = useState<string[]>(
    () => client?.certificateIds ?? [],
  );
  const selectedCertificateIds = keepSelectable(
    certificateIds,
    selectableCertificateIds,
  );

  const onScopeChange = (
    oAuthId: string,
    account: string,
    checked: boolean,
  ) => {
    setSelectedScopes((prev) => toggleScope(prev, oAuthId, account, checked));
  };

  const onCertificateChange = (certificateId: string, checked: boolean) => {
    if (
      checked &&
      selectableCertificateIds &&
      !selectableCertificateIds.includes(certificateId)
    ) {
      return;
    }

    setCertificateIds((prev) =>
      checked
        ? prev.includes(certificateId)
          ? prev
          : [...prev, certificateId]
        : prev.includes(certificateId)
          ? prev.filter((id) => id !== certificateId)
          : prev,
    );
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();

    const values: ClientFormValues = {
      displayName,
      certificateIds: selectedCertificateIds,
      scopes: selectedScopes,
    };

    fetcher.submit(values, { method: "post", encType: "application/json" });
  };

  const failure = isClientActionFailure(fetcher.data) ? fetcher.data : null;
  const error = failure?.missingLink
    ? translate(missingEndpointTextKey)
    : (failure?.message ?? null);

  return {
    displayName,
    setDisplayName,
    selectedScopes,
    onScopeChange,
    hasSelectedScopes: hasAnyScope(selectedScopes),
    selectedCertificateIds,
    onCertificateChange,
    submitting: fetcher.state !== "idle",
    error,
    onSubmit,
  };
};
